import { Link, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Fuel, Radio, Bell, RefreshCw, Receipt } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCompanyContext } from '@/hooks/use-company-context';
import { useNotificationPreferences, NotificationPreferences as NotifPrefs } from '@/hooks/useNotificationPreferences';

export default function NotificationPreferences() {
  const { slug } = useParams<{ slug: string }>();
  const { companyId } = useCompanyContext();
  const { preferences, isLoading, updatePreferences, isUpdating } = useNotificationPreferences(companyId);
  
  const handleToggle = (key: keyof NotifPrefs, value: boolean | number) => {
    updatePreferences({ [key]: value });
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/company/${slug}/admin/systems/fleet/notifications`}>
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Preferências de Notificação</h1>
          <p className="text-muted-foreground">
            Configure quais notificações você deseja receber
          </p>
        </div>
      </div>
      
      {isLoading ? (
        <Card className="p-12">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto" />
        </Card>
      ) : (
        <>
          {/* Marking Notifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Fuel className="h-5 w-5" />
                Notificações de Marcações
              </CardTitle>
              <CardDescription>
                Receba notificações quando motoristas fizerem marcações
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Marcações de Combustível</p>
                  <p className="text-sm text-muted-foreground">
                    Notificar quando combustível for marcado
                  </p>
                </div>
                <Switch
                  checked={preferences?.marking_fuel_enabled}
                  onCheckedChange={(v) => handleToggle('marking_fuel_enabled', v)}
                  disabled={isUpdating}
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Marcações de Odômetro</p>
                  <p className="text-sm text-muted-foreground">
                    Notificar quando odômetro for marcado
                  </p>
                </div>
                <Switch
                  checked={preferences?.marking_odometer_enabled}
                  onCheckedChange={(v) => handleToggle('marking_odometer_enabled', v)}
                  disabled={isUpdating}
                />
              </div>
            </CardContent>
          </Card>
          
          {/* GPS Notifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Radio className="h-5 w-5 text-green-600" />
                Notificações GPS
              </CardTitle>
              <CardDescription>
                Receba notificações sobre eventos GPS
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Webhooks</p>
                  <p className="text-sm text-muted-foreground">
                    Notificações via webhook
                  </p>
                </div>
                <Switch
                  checked={preferences?.gps_webhook_enabled}
                  onCheckedChange={(v) => handleToggle('gps_webhook_enabled', v)}
                  disabled={isUpdating}
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">E-mails</p>
                  <p className="text-sm text-muted-foreground">
                    Notificações por e-mail
                  </p>
                </div>
                <Switch
                  checked={preferences?.gps_email_enabled}
                  onCheckedChange={(v) => handleToggle('gps_email_enabled', v)}
                  disabled={isUpdating}
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Telegram</p>
                  <p className="text-sm text-muted-foreground">
                    Notificações via Telegram
                  </p>
                </div>
                <Switch
                  checked={preferences?.gps_telegram_enabled}
                  onCheckedChange={(v) => handleToggle('gps_telegram_enabled', v)}
                  disabled={isUpdating}
                />
              </div>
            </CardContent>
          </Card>
          
          {/* System Notifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notificações do Sistema
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Anúncios</p>
                  <p className="text-sm text-muted-foreground">
                    Novidades e atualizações do sistema
                  </p>
                </div>
                <Switch
                  checked={preferences?.system_announcements_enabled}
                  onCheckedChange={(v) => handleToggle('system_announcements_enabled', v)}
                  disabled={isUpdating}
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Avisos Críticos</p>
                  <p className="text-sm text-muted-foreground">
                    Alertas importantes do sistema
                  </p>
                </div>
                <Switch
                  checked={preferences?.system_warnings_enabled}
                  onCheckedChange={(v) => handleToggle('system_warnings_enabled', v)}
                  disabled={isUpdating}
                />
              </div>
            </CardContent>
          </Card>
          
          {/* Expense Notifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-orange-600" />
                Notificações de Despesas
              </CardTitle>
              <CardDescription>
                Receba alertas sobre despesas vencendo ou vencidas
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Despesas Vencendo</p>
                  <p className="text-sm text-muted-foreground">
                    Notificar quando despesas estão próximas do vencimento
                  </p>
                </div>
                <Switch
                  checked={preferences?.expenses_due_soon_enabled}
                  onCheckedChange={(v) => handleToggle('expenses_due_soon_enabled', v)}
                  disabled={isUpdating}
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Despesas Vencidas</p>
                  <p className="text-sm text-muted-foreground">
                    Notificar sobre despesas que já venceram
                  </p>
                </div>
                <Switch
                  checked={preferences?.expenses_overdue_enabled}
                  onCheckedChange={(v) => handleToggle('expenses_overdue_enabled', v)}
                  disabled={isUpdating}
                />
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Dias de Antecedência para Alerta
                </label>
                <p className="text-xs text-muted-foreground mb-2">
                  Quantos dias antes do vencimento você quer ser notificado?
                </p>
                <Select
                  value={preferences?.expenses_due_days_before?.toString() || '3'}
                  onValueChange={(v) => handleToggle('expenses_due_days_before', parseInt(v))}
                  disabled={isUpdating}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 dia antes</SelectItem>
                    <SelectItem value="2">2 dias antes</SelectItem>
                    <SelectItem value="3">3 dias antes</SelectItem>
                    <SelectItem value="5">5 dias antes</SelectItem>
                    <SelectItem value="7">7 dias antes</SelectItem>
                    <SelectItem value="15">15 dias antes</SelectItem>
                    <SelectItem value="30">30 dias antes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
          
          {/* Delivery Methods */}
          <Card>
            <CardHeader>
              <CardTitle>Métodos de Entrega</CardTitle>
              <CardDescription>
                Escolha como receber as notificações
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">E-mail</p>
                  <p className="text-sm text-muted-foreground">
                    Receber notificações por e-mail
                  </p>
                </div>
                <Switch
                  checked={preferences?.email_enabled}
                  onCheckedChange={(v) => handleToggle('email_enabled', v)}
                  disabled={isUpdating}
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Push (Navegador)</p>
                  <p className="text-sm text-muted-foreground">
                    Notificações push no navegador
                  </p>
                </div>
                <Switch
                  checked={preferences?.push_enabled}
                  onCheckedChange={(v) => handleToggle('push_enabled', v)}
                  disabled={isUpdating}
                />
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
